import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { IDefaults } from '../../modules/API/Defaults/models/defaults'
import { TYPES_OF_BM } from '../modals/BrodMigdalModal/helpers/typeOfBM'
import { RootState } from '../../../store/reducers/rootReducer'

export interface IBrodMigdalKey {
    id: number
    subId?: string | number
    userName?: string
}

export type TBrodMigdalValue = number | string | boolean | undefined

const trimmed = (value: unknown): string => String(value ?? '').trim()

export const findBrodMigdalRecord = (
    defaults: IDefaults[] | null,
    key: IBrodMigdalKey,
    currentUserName?: string | null
): IDefaults | null => {
    if (!defaults) return null

    let filtered = defaults.filter((item) => item.Int_ID === key.id)
    if (key.subId !== undefined) {
        filtered = filtered.filter((item) => trimmed(item.Sub_ID) === trimmed(key.subId))
    }
    if (filtered.length === 0) return null
    if (filtered.length === 1) return filtered[0]

    const userName = trimmed(key.userName ?? currentUserName)
    const byUser = filtered.find((item) => trimmed(item.UserName) === userName)
    if (byUser) return byUser
    // No personalized match — fall back to the global record (blank UserName),
    // otherwise to the first filtered one.
    return filtered.find((item) => trimmed(item.UserName) === '') ?? filtered[0]
}

export const readBrodMigdalValue = (record: IDefaults | null): TBrodMigdalValue => {
    if (!record) return undefined
    const field = TYPES_OF_BM[trimmed(record.Type) as keyof typeof TYPES_OF_BM]
    if (!field) return undefined
    return record[field]
}

export interface UseBrodMigdalReturn {
    getBrodMigdal: (key: IBrodMigdalKey) => TBrodMigdalValue
    getBrodMigdalRecord: (key: IBrodMigdalKey) => IDefaults | null
}

export const useBrodMigdal = (): UseBrodMigdalReturn => {
    const defaults = useSelector((state: RootState): IDefaults[] | null => state.defaults?.defaults ?? null)
    const userName = useSelector((state: RootState): string | undefined => state.mainReducer.user?.name)

    const getBrodMigdalRecord = useCallback(
        (key: IBrodMigdalKey) => findBrodMigdalRecord(defaults, key, userName),
        [defaults, userName]
    )
    const getBrodMigdal = useCallback(
        (key: IBrodMigdalKey) => readBrodMigdalValue(findBrodMigdalRecord(defaults, key, userName)),
        [defaults, userName]
    )

    return { getBrodMigdal, getBrodMigdalRecord }
}
